import ReactDOM from "react-dom/client"

import store from "./store/store"
import { Provider } from "react-redux"

import App from "./App"

import * as serviceWorker from "./serviceWorker"

import { alpha, createTheme, StyledEngineProvider, ThemeProvider } from "@mui/material/styles"

import { BrowserRouter } from "react-router-dom"

import "@fontsource-variable/inter"
import "./index.css"

const theme = createTheme(
  {
    typography: {
      fontFamily: "'Inter Variable', Inter, Roboto, sans-serif"
    },
    spacing: 8,
    palette: {
      primary: {
        light: "#9290C9",
        main: "#4D4B90",
        dark: "#373566",
        contrastText: "#FFFFFF"
      },
      secondary: {
        light: "#FAD87C",
        main: "#F7B418",
        dark: "#B88412",
        contrastText: "#FFFFFF"
      },
      // Per-specialist accent colors, used across the Analyse/Account/Upload screens to give each
      // specialist (diver, fisher, aquarist, scientist) its own recognizable identity, layered on top of
      // the brand primary/secondary rather than replacing them.
      specialists: {
        diver: { light: "#6FB3D2", main: "#2E86AB", dark: "#1B5E7A", contrastText: "#FFFFFF" },
        fisher: { light: "#F0A868", main: "#E07A3E", dark: "#A85826", contrastText: "#FFFFFF" },
        aquarist: { light: "#6BC7AE", main: "#2E9E83", dark: "#1F6E5A", contrastText: "#FFFFFF" },
        scientist: { light: "#C97FAE", main: "#9B3F79", dark: "#6B2A54", contrastText: "#FFFFFF" },
        // Tank management is part of the Aquarist workflow, not a specialist role of its
        // own, it deliberately shares Aquarist's color rather than getting a new one.
        "aquarist-admin": { light: "#6BC7AE", main: "#2E9E83", dark: "#1F6E5A", contrastText: "#FFFFFF" }
      },
      DataGrid: {
        bg: "transparent",
        headerBg: "#ffffff",
        pinnedBg: "#f1f1f1"
      }
    },
    components: {
      MuiButton: {
        styleOverrides: {
          /**
           * Knoppenstandaard. Wat hier staat geldt voor elke knop, zodat twee knoppen naast elkaar
           * zich hetzelfde gedragen; een `sx` op één knop hoort daar niet van af te wijken.
           *
           * - Hover: altijd een donkerdere tint van de eigen kleur, geen zetje omhoog. Een gevulde
           *   knop wordt `dark`, een omlijnde of tekstknop krijgt een tint van zijn kleur en een
           *   donkerdere rand. De standaard van MUI (4% tint) was op de grijze achtergrond nauwelijks
           *   te zien.
           * - Schaduw alleen op gevulde knoppen. Omlijnde en tekstknoppen blijven vlak; met schaduw
           *   leest een tekstknop als een grijs blok.
           * - Letterdikte 600 voor alle knoppen.
           *
           * Welke variant waarvoor (zet dat bij de knop zelf, niet hier):
           * - de primaire handeling van een scherm of dialoog: `contained`, hooguit één per groep;
           * - een secundaire handeling: `outlined` in de primaire kleur;
           * - annuleren, sluiten, deselecteren: `outlined` met `color="inherit"`;
           * - licht (terug, tonen/verbergen, meer tonen, filters wissen): `text`;
           * - destructief: `color="error"`, gevuld alleen in de bevestigingsdialoog.
           * Formaat: medium; `small` alleen in kaarten, tabellen, meldingen en popovers.
           * Bezig: een rondje als `startIcon` (in plaats van het icoon) en de knop uit.
           */
          root: ({ theme }) => {
            const colors = ["primary", "secondary", "error", "warning", "info", "success"]
              .filter(color => theme.palette[color]?.main)

            return {
              textTransform: "unset",
              fontWeight: 600,
              variants: [
                {
                  props: { variant: "contained" },
                  style: {
                    boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
                    "&:hover": {
                      boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)"
                    },
                    "&:active": {
                      boxShadow: "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)"
                    }
                  }
                },
                { props: { variant: "outlined" }, style: { boxShadow: "none" } },
                { props: { variant: "text" }, style: { boxShadow: "none" } },
                // MUI zet de hoverkleur via CSS-variabelen per kleur; die geven we hier een
                // krachtiger tint. Zelfde variabelen, dus ook `disabled` en `focus` blijven kloppen.
                ...colors.map(color => ({
                  props: { color },
                  style: {
                    "@media (hover: hover)": {
                      "&:hover": {
                        "--variant-containedBg": theme.palette[color].dark,
                        "--variant-textBg": alpha(theme.palette[color].main, 0.12),
                        "--variant-outlinedBg": alpha(theme.palette[color].main, 0.12),
                        "--variant-outlinedBorder": theme.palette[color].dark
                      }
                    }
                  }
                })),
                {
                  props: { color: "inherit" },
                  style: {
                    "@media (hover: hover)": {
                      "&:hover": {
                        "--variant-textBg": alpha(theme.palette.text.primary, 0.1),
                        "--variant-outlinedBg": alpha(theme.palette.text.primary, 0.1)
                      }
                    }
                  }
                }
              ]
            }
          },
          disableElevation: {
            boxShadow: "unset",
            "&:active": {
              boxShadow: "unset"
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          // Zelfde regel als bij `MuiButton`: bij hover een donkerdere tint van de eigen kleur. Via
          // `currentColor`, zodat een wit icoon op een donkere kop even goed reageert als een grijs
          // icoon op een kaart. De 4% van MUI was nauwelijks te zien.
          root: {
            "--IconButton-hoverBg": "color-mix(in srgb, currentColor 14%, transparent)"
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            "&.Mui-disabled": {
              background: "#eeeeee"
            }
          }
        }
      },
      MuiFab: {
        styleOverrides: {
          extended: {
            padding: "4px 12px 4px 16px"
          },
          root: {
            boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
            textTransform: "unset",
            "&:active": {
              boxShadow: "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)"
            }
          }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          // Een uitgeschakelde toggle die toevallig de gekozen waarde is, bleef de accentkleur van
          // zijn `color`-variant houden: MUI zet `&.Mui-selected` ná `&.Mui-disabled`, en bij gelijke
          // specificiteit wint de laatste regel. Op een alleen-lezen formulier stond daarmee één
          // veld in kleur terwijl alle andere velden grijs waren, wat leest als "dit is nog wel
          // aanpasbaar". Twee klassen hier winnen het van MUI's ene.
          root: ({ theme }) => ({
            "&.Mui-disabled.Mui-selected": {
              color: theme.palette.action.disabled,
              backgroundColor: theme.palette.action.disabledBackground
            }
          })
        }
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            margin: "8px 0 0 0",
            "&:before": {
              display: "none"
            },
            "&:first-of-type": {
              marginTop: 0
            },
            "&.Mui-expanded": {
              margin: "8px 0 0 0"
            }
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            // padding: "0 8px",
            minHeight: 48,
            height: 48,
            "&.Mui-expanded": {
              minHeight: 48,
              height: 48
            }
          },
          content: {
            margin: "auto 0 !important",
            alignItems: "center"
          },
          expandIconWrapper: {
            padding: 4,
            margin: "0 -8px 0 0"
          }
        }
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            display: "flex",
            flexDirection: "column",
            padding: "0 8px 8px"
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            display: "flex",
            flexDirection: "column"
          }
        }
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            height: 6
          },
          rail: {
            opacity: 0.3
          },
          thumb: ({ theme }) => ({
            width: 20,
            height: 20,
            boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            "&:hover, &.Mui-focusVisible": {
              boxShadow: `0 2px 6px rgba(0,0,0,0.25), 0 0 0 8px ${alpha(theme.palette.primary.main, 0.16)}`
            },
            "&.Mui-active": {
              boxShadow: `0 2px 6px rgba(0,0,0,0.25), 0 0 0 10px ${alpha(theme.palette.primary.main, 0.16)}`
            }
          }),
          valueLabel: {
            fontWeight: 600
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" },
              "&.Mui-focusVisible": { background: "rgba(0, 0, 0, 0.08)" }
            }
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        }
      },
      MuiCheckbox: {
        defaultProps: {
          disableFocusRipple: true,
          disableTouchRipple: true,
          disableRipple: true
        }
      },
      MuiTooltip: {
        defaultProps: {
          disableFocusListener: true,
          disableInteractive: true
        }
      }
    }
  }
)

const root = ReactDOM.createRoot(document.getElementById("root"))

root.render(
  <StyledEngineProvider injectFirst>
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ThemeProvider>
    </Provider>
  </StyledEngineProvider>
)

serviceWorker.unregister()